// Airport definitions with multiple identifiers
const AIRPORTS = [
  {
    name: 'London Heathrow',
    coordinates: { lat: 51.4700, lng: -0.4543 },
    identifiers: [
      'heathrow',
      'lhr',
      'terminals? [1-5]',
      'terminal [1-5]',
      'london heathrow',
      'tw6',  // Heathrow postcode area
    ]
  },
  {
    name: 'London Gatwick',
    coordinates: { lat: 51.1537, lng: -0.1821 },
    identifiers: [
      'gatwick',
      'lgw',
      'north terminal',
      'south terminal',
      'london gatwick',
      'rh6',  // Gatwick postcode area
    ]
  },
  {
    name: 'London Stansted',
    coordinates: { lat: 51.8860, lng: 0.2389 },
    identifiers: [
      'stansted',
      'stn',
      'london stansted',
      'cm24',  // Stansted postcode area
    ]
  },
  {
    name: 'London Luton',
    coordinates: { lat: 51.8763, lng: -0.3717 },
    identifiers: [
      'luton airport',
      'ltn',
      'london luton',
      'lu2',  // Luton airport postcode area
    ]
  }
];

const AIRPORT_RADIUS_MILES = 5; // Detection radius around airports

// Pricing constants
const PRICING = {
  // Base Rates
  STANDARD_SINGLE_HOUR_RATE: 170,
  STANDARD_MULTI_HOUR_RATE: 110,
  AIRPORT_FLAT_RATE: 175,
  AIRPORT_MULTI_HOUR_RATE: 122.5,

  // Discounts
  LONG_BOOKING_DISCOUNT: 0.10,
  LONG_BOOKING_HOURS_THRESHOLD: 3,

  // Dynamic Surcharge Triggers
  SURCHARGE_TIME_THRESHOLD_MINUTES: 100,
  SURCHARGE_DISTANCE_THRESHOLD_MILES: 30,

  // Dynamic Surcharge Rates
  SURCHARGE_TIME_RATE_PER_HOUR: 10, // £10 driver + £35 business
  SURCHARGE_FUEL_RATE_PER_MILE: 0.20,

  // Unsociable Hours
  UNSOCIABLE_HOURS_SURCHARGE: 25,
  UNSOCIABLE_HOURS_START: 0,   // Midnight
  UNSOCIABLE_HOURS_END: 6      // 6 AM
};

/**
 * Calculate the distance between two points using the Haversine formula
 */
const calculateDistance = (point1, point2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (point2.lat - point1.lat) * Math.PI / 180;
  const dLon = (point2.lng - point1.lng) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Check if a location matches any airport identifiers
 */
const matchAirportIdentifiers = (location) => {
  const searchText = [
    location.address,
    location.name,
    location.postcode,
    location.placeId,
    location.formatted_address,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  for (const airport of AIRPORTS) {
    const matched = airport.identifiers.some(identifier => {
      const regex = new RegExp(identifier.toLowerCase());
      return regex.test(searchText);
    });

    if (matched) {
      return {
        name: airport.name,
        coordinates: airport.coordinates,
        matchType: 'identifier'
      };
    }
  }

  return null;
};

/**
 * Check if a location is near an airport using coordinates
 */
const checkNearbyAirport = (coordinates) => {
  if (!coordinates?.lat || !coordinates?.lng) return null;

  for (const airport of AIRPORTS) {
    const distance = calculateDistance(coordinates, airport.coordinates);
    if (distance <= AIRPORT_RADIUS_MILES) {
      return {
        name: airport.name,
        distance: Math.round(distance * 10) / 10,
        matchType: 'proximity'
      };
    }
  }
  return null;
};

/**
 * Calculate the total price for a booking based on the new unified model.
 * @param {object} bookingDetails - The details of the booking.
 * @param {object} bookingDetails.pickupLocation - The pickup location object.
 * @param {object} bookingDetails.dropoffLocation - The dropoff location object.
 * @param {number} bookingDetails.durationHours - The duration of the booking in hours.
 * @param {string} bookingDetails.pickupTime - The pickup time in HH:MM format.
 * @param {object} journeyMetrics - The calculated metrics for the round trip.
 * @param {number} journeyMetrics.roundTripTimeMinutes - The total round trip time.
 * @param {number} journeyMetrics.roundTripDistanceMiles - The total round trip distance.
 * @returns {object} An object containing the final price and a breakdown.
 */
const calculateBookingPrice = ({ 
  pickupLocation, 
  dropoffLocation, 
  durationHours,
  pickupTime,
  journeyMetrics 
}) => {
  if (!pickupLocation || !dropoffLocation || !durationHours || !pickupTime || !journeyMetrics) {
    throw new Error('Invalid or incomplete booking details provided for pricing');
  }

  const appliedRules = [];
  let finalPrice = 0;

  // --- Component 1: The Base Rate ---
  const isAirportTransfer = Boolean(
    matchAirportIdentifiers(pickupLocation) || 
    checkNearbyAirport(pickupLocation.coordinates) ||
    matchAirportIdentifiers(dropoffLocation) ||
    checkNearbyAirport(dropoffLocation.coordinates)
  );

  let baseRate = 0;
  if (isAirportTransfer) {
    baseRate = durationHours === 1 
      ? PRICING.AIRPORT_FLAT_RATE 
      : durationHours * PRICING.AIRPORT_MULTI_HOUR_RATE;
    appliedRules.push(durationHours === 1 ? 'Airport flat rate applied' : 'Airport multi-hour rate applied');
  } else {
    baseRate = durationHours === 1 
      ? PRICING.STANDARD_SINGLE_HOUR_RATE 
      : durationHours * PRICING.STANDARD_MULTI_HOUR_RATE;
    appliedRules.push(durationHours === 1 ? 'Standard single hour rate applied' : 'Standard multi-hour rate applied');
  }
  finalPrice += baseRate;

  // --- Component 2: The Long Booking Discount ---
  let discountAmount = 0;
  if (durationHours >= PRICING.LONG_BOOKING_HOURS_THRESHOLD) {
    discountAmount = baseRate * PRICING.LONG_BOOKING_DISCOUNT;
    finalPrice -= discountAmount;
    appliedRules.push(`Long booking discount applied (${PRICING.LONG_BOOKING_DISCOUNT * 100}% off)`);
  }

  // --- Component 3: The Dynamic Travel Surcharge ---
  let travelSurcharge = 0;
  const { roundTripTimeMinutes, roundTripDistanceMiles } = journeyMetrics;

  if (roundTripTimeMinutes > PRICING.SURCHARGE_TIME_THRESHOLD_MINUTES ||
      roundTripDistanceMiles > PRICING.SURCHARGE_DISTANCE_THRESHOLD_MILES) {
        
    const timeSurcharge = (roundTripTimeMinutes / 60) * PRICING.SURCHARGE_TIME_RATE_PER_HOUR;
    const fuelSurcharge = roundTripDistanceMiles * PRICING.SURCHARGE_FUEL_RATE_PER_MILE;
    travelSurcharge = timeSurcharge + fuelSurcharge;
    finalPrice += travelSurcharge;
    appliedRules.push('Dynamic travel surcharge applied for time and distance');
  }

  // --- Component 4: The Unsociable Hours Surcharge ---
  let unsociableHoursSurcharge = 0;
  const pickupHour = parseInt(pickupTime.split(':')[0], 10);
  if (pickupHour >= PRICING.UNSOCIABLE_HOURS_START && pickupHour < PRICING.UNSOCIABLE_HOURS_END) {
    unsociableHoursSurcharge = PRICING.UNSOCIABLE_HOURS_SURCHARGE;
    finalPrice += unsociableHoursSurcharge;
    appliedRules.push('Unsociable hours surcharge applied');
  }
  
  const breakdown = {
    baseRate: Math.round(baseRate),
    discountAmount: Math.round(discountAmount),
    travelSurcharge: Math.round(travelSurcharge),
    unsociableHoursSurcharge: unsociableHoursSurcharge,
    finalPrice: Math.round(finalPrice)
  };

  // Log the detailed breakdown for testing
  console.log('%c--- Pricing Calculation ---', 'color: #1a237e; font-weight: bold;');
  console.log(`1. Base Rate: £${breakdown.baseRate.toFixed(2)} (${isAirportTransfer ? 'Airport' : 'Standard'})`);
  console.log(`2. Long Booking Discount: -£${breakdown.discountAmount.toFixed(2)}`);
  console.log('---');
  console.log(`   Subtotal after discount: £${(breakdown.baseRate - breakdown.discountAmount).toFixed(2)}`);
  console.log('---');
  console.log(`3. Dynamic Surcharge: +£${breakdown.travelSurcharge.toFixed(2)}`);
  if (breakdown.travelSurcharge > 0) {
    const timeSurcharge = (journeyMetrics.roundTripTimeMinutes / 60) * PRICING.SURCHARGE_TIME_RATE_PER_HOUR;
    const fuelSurcharge = journeyMetrics.roundTripDistanceMiles * PRICING.SURCHARGE_FUEL_RATE_PER_MILE;
    console.log(`   - Time Cost: (${journeyMetrics.roundTripTimeMinutes} mins / 60) * £${PRICING.SURCHARGE_TIME_RATE_PER_HOUR} = £${timeSurcharge.toFixed(2)}`);
    console.log(`   - Fuel Cost: ${journeyMetrics.roundTripDistanceMiles} miles * £${PRICING.SURCHARGE_FUEL_RATE_PER_MILE} = £${fuelSurcharge.toFixed(2)}`);
  }
  console.log(`   (Triggered by: ${roundTripTimeMinutes} mins & ${roundTripDistanceMiles} miles)`);
  console.log(`4. Unsociable Hours: +£${breakdown.unsociableHoursSurcharge.toFixed(2)}`);
  console.log('---');
  console.log(`%cFinal Price: £${breakdown.finalPrice.toFixed(2)}`, 'color: #28a745; font-weight: bold;');

  return {
    totalPrice: breakdown.finalPrice,
    isAirportTransfer,
    appliedRules,
    breakdown: {
      baseRate: Math.round(baseRate),
      discountAmount: Math.round(discountAmount),
      travelSurcharge: Math.round(travelSurcharge),
      unsociableHoursSurcharge: unsociableHoursSurcharge,
    },
    debug: {
      journeyMetrics
    }
  };
};

module.exports = { calculateBookingPrice, matchAirportIdentifiers, checkNearbyAirport }; 